import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  companySizeLabel,
  dealStageLabel,
  industryLabel,
  lifecycleStageLabel,
  type CrmAccount,
} from '@/lib/crm-account';
import { RowCrmAction } from '@/components/views/atoms/RowCrmAction';
import { cn } from '@/lib/utils';

export interface AccountAppChoice {
  key: string;
  label: string;
  onSelect: () => void;
}

const CLOSED_STAGES = new Set(['closedwon', 'closedlost']);

/** Site, activity, size, city, stage, open deal, owner: what HubSpot says, nothing guessed. */
export const accountHeaderFacts = (account: CrmAccount | null | undefined): string[] => {
  if (!account || account.status !== 'synced') return [];
  const { company } = account;
  const openDeal = account.deals.find((deal) => deal.stage && !CLOSED_STAGES.has(deal.stage));
  const dealStage = openDeal ? dealStageLabel(openDeal.stage) : null;
  return [
    (company.domain ?? company.website ?? null)?.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '') ?? null,
    industryLabel(company.industry),
    companySizeLabel(company.employees, company.employeeRange),
    company.city,
    lifecycleStageLabel(company.lifecycleStage),
    dealStage ? `Affaire : ${dealStage}` : null,
    company.ownerName ?? company.ownerEmail ?? null,
  ].filter((value): value is string => Boolean(value));
};

/**
 * The top of an account's page (26/10/2026): who the company is, in one line
 * of data from HubSpot, or « Pas dans HubSpot » with « Ajouter au CRM » when a
 * run about it can carry the request. One button opens the apps that start
 * from an account; free work goes through the chat, which reads the account.
 */
export const AccountHeader = ({
  title,
  back,
  account,
  crmError,
  addToCrmRunId,
  apps,
  attach,
}: {
  title: string;
  back?: ReactNode;
  /** Undefined while HubSpot is not read yet; null when the account is not in HubSpot. */
  account: CrmAccount | null | undefined;
  crmError: string | null;
  /** The latest run about this account, the source of « Ajouter au CRM ». */
  addToCrmRunId: string | null;
  apps: AccountAppChoice[];
  /** « Rattacher à… »: for a company known only by its name. */
  attach?: ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  const menu = useRef<HTMLDivElement>(null);
  const facts = accountHeaderFacts(account);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event: MouseEvent | KeyboardEvent) => {
      if (event instanceof KeyboardEvent ? event.key === 'Escape' : !menu.current?.contains(event.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', close);
    window.addEventListener('keydown', close);
    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('keydown', close);
    };
  }, [open]);

  return (
    <header className="mb-7 flex flex-wrap items-start justify-between gap-4 border-b border-domino-cardBorder pb-6">
      <div className="min-w-0">
        {back}
        <h1 className="font-display text-[28px] font-semibold tracking-tight text-domino-ink">{title}</h1>
        {facts.length > 0 && (
          <p className="mt-1.5 flex flex-wrap gap-x-2 text-sm leading-6 text-domino-inkSoft">
            {facts.map((fact, index) => (
              <span key={fact} className="whitespace-nowrap">
                {index > 0 && <span aria-hidden className="mr-2">·</span>}
                {fact}
              </span>
            ))}
          </p>
        )}
        {account === null && !crmError && (
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 text-sm text-domino-inkSoft">
            <span>Pas dans HubSpot</span>
            {attach}
            {addToCrmRunId && <RowCrmAction sourceRunId={addToCrmRunId} rowLabel={title} className="" />}
          </div>
        )}
      </div>
      {apps.length > 0 && (
        <div ref={menu} className="relative">
          <button
            type="button"
            aria-haspopup="menu"
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
            className="inline-flex h-9 items-center gap-1.5 rounded-xl border border-domino-cardBorder bg-domino-white px-3.5 text-sm font-medium text-domino-ink transition-[background-color,transform] duration-150 ease-snap hover:bg-domino-muted active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-domino-prune"
          >
            Utiliser une app
            <ChevronDown className={cn('h-4 w-4 transition-transform duration-150', open && 'rotate-180')} />
          </button>
          {open && (
            <div role="menu" className="absolute right-0 top-11 z-30 w-60 origin-top-right overflow-hidden rounded-2xl border border-domino-cardBorder bg-domino-white py-1 shadow-[var(--domino-shadow-elevated)] motion-safe:animate-fadeIn">
              {apps.map((app) => (
                <button
                  key={app.key}
                  type="button"
                  role="menuitem"
                  onClick={() => { setOpen(false); app.onSelect(); }}
                  className="flex w-full items-center px-3.5 py-2 text-left text-sm text-domino-ink hover:bg-domino-muted focus-visible:bg-domino-muted focus-visible:outline-none"
                >
                  {app.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </header>
  );
};
